'use client';

import * as React from 'react';
import PropTypes from 'prop-types';
import { useMenuTrigger } from './useMenuTrigger.js';
import { useMenuRootContext } from '../root/MenuRootContext.js';
import { pressableTriggerOpenStateMapping } from '../../utils/popupStateMapping.js';
import { useComponentRenderer } from '../../utils/useComponentRenderer.js';
import { mergeProps } from '../../merge-props/index.js';

/**
 * A button that opens the menu.
 * Renders a `<button>` element.
 *
 * Documentation: [Base UI Menu](https://base-ui.com/react/components/menu)
 */
const MenuTrigger = /*#__PURE__*/React.forwardRef(function MenuTrigger(props, forwardedRef) {
  const {
    render,
    className,
    disabled = false,
    ...other
  } = props;
  const {
    triggerProps: rootTriggerProps,
    disabled: menuDisabled,
    setTriggerElement,
    open,
    mounted,
    setOpen,
    allowMouseUpTriggerRef,
    positionerRef
  } = useMenuRootContext();
  const {
    getTriggerProps
  } = useMenuTrigger({
    disabled: disabled || menuDisabled,
    rootRef: forwardedRef,
    setTriggerElement,
    open,
    setOpen,
    allowMouseUpTriggerRef,
    positionerRef
  });
  const state = React.useMemo(() => ({
    disabled,
    open: mounted
  }), [disabled, mounted]);
  const propGetter = React.useCallback(externalProps => mergeProps(rootTriggerProps, externalProps, getTriggerProps), [getTriggerProps, rootTriggerProps]);
  const {
    renderElement
  } = useComponentRenderer({
    render: render || 'button',
    className,
    state,
    propGetter,
    customStyleHookMapping: pressableTriggerOpenStateMapping,
    extraProps: other
  });
  return renderElement();
});
process.env.NODE_ENV !== "production" ? MenuTrigger.propTypes /* remove-proptypes */ = {
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * @ignore
   */
  children: PropTypes.node,
  /**
   * CSS class applied to the element, or a function that
   * returns a class based on the component’s state.
   */
  className: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  /**
   * Whether the component should ignore user interaction.
   * @default false
   */
  disabled: PropTypes.bool,
  /**
   * Allows you to replace the component’s HTML element
   * with a different tag, or compose it with another component.
   *
   * Accepts a `ReactElement` or a function that returns the element to render.
   */
  render: PropTypes.oneOfType([PropTypes.element, PropTypes.func])
} : void 0;
export { MenuTrigger };